// free-gift-claim.js
// Handles the "CLAIM FREE GIFT" button functionality
// Adds Fresh Awakening product to cart when threshold is met
// Also updates callout state when cart total changes

(function () {
  'use strict';

  function initFreeGiftClaim() {
    const claimButtons = document.querySelectorAll('.claim-free-gift-btn');
    
    claimButtons.forEach((button) => {
      // Only attach listener if not already attached (use data attribute to track)
      if (button.dataset.listenerAttached === 'true') {
        return;
      }
      
      button.dataset.listenerAttached = 'true';
      
      // Use click event (works for both mouse and touch on modern browsers)
      // Mobile browsers will fire click after touchend, so we don't need separate handlers
      button.addEventListener('click', handleClaimClick, { passive: false });
    });

    // Update callout state based on current cart total (for dynamic updates)
    // Only if callout exists (don't force update if it's not there)
    const callout = document.querySelector('.free-gift-callout');
    if (callout) {
      updateCalloutState();
    }
  }

  let updateCalloutTimeout = null;
  let lastCartTotal = null;

  function updateCalloutState() {
    const callout = document.querySelector('.free-gift-callout');
    if (!callout) return;

    // Debounce rapid updates
    if (updateCalloutTimeout) {
      clearTimeout(updateCalloutTimeout);
    }

    updateCalloutTimeout = setTimeout(() => {
      const threshold = parseInt(callout.dataset.threshold) || 0;
      const currency = callout.dataset.currency || 'USD';

      // Get current cart total
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const totalCents = cart.total_price || 0;
          
          // Skip update if cart total hasn't changed
          if (lastCartTotal === totalCents) {
            return;
          }
          lastCartTotal = totalCents;

          const meetsThreshold = totalCents >= threshold;
          const remainingAmount = Math.max(0, threshold - totalCents);

          // Check if gift is already in cart (use the configured product handle)
          const giftHandle = callout.dataset.giftProductHandle;
          const giftInCart = giftHandle
            ? cart.items.some(item => item.product_handle === giftHandle)
            : false;

          // If gift is in cart, hide callout (it will be removed by server-side rendering)
          if (giftInCart) {
            callout.style.display = 'none';
            return;
          }

          // Update callout display
          callout.style.display = '';
          callout.dataset.total = totalCents;

          const messageEl = callout.querySelector('.free-gift-message');
          const buttonEl = callout.querySelector('.claim-free-gift-btn');
          const priceEl = callout.querySelector('.price.price--end');

          if (meetsThreshold) {
            // Show button
            if (messageEl) {
              const readyEl = messageEl.querySelector('.free-gift-ready');
              if (!readyEl) {
                messageEl.innerHTML = '<span class="free-gift-ready">Free Gift Unlocked</span>';
              } else {
                readyEl.textContent = 'Free Gift Unlocked';
              }
            }
            if (buttonEl) {
              buttonEl.style.display = '';
              buttonEl.disabled = false;
              buttonEl.classList.remove('loading');
              const buttonSpan = buttonEl.querySelector('span');
              if (buttonSpan) {
                buttonSpan.textContent = 'CLAIM GIFT';
              } else {
                buttonEl.textContent = 'CLAIM GIFT';
              }
            }
            if (priceEl) {
              priceEl.style.display = 'none';
            }
          } else {
            // Show remaining amount message
            if (messageEl) {
              const remainingEl = messageEl.querySelector('.free-gift-remaining');
              if (remainingEl) {
                remainingEl.textContent = `Add ${formatMoney(remainingAmount, currency)} more to unlock free gift`;
              } else {
                messageEl.innerHTML = `<span class="free-gift-remaining">Add ${formatMoney(remainingAmount, currency)} more to unlock free gift</span>`;
              }
            }
            if (buttonEl) {
              buttonEl.style.display = 'none';
            }
            if (priceEl) {
              priceEl.style.display = '';
            }
          }
        })
        .catch(error => {
          console.error('[free-gift-claim] Error updating callout state:', error);
        });
    }, 150);
  }

  function formatMoney(cents, currency) {
    // Simple money formatting - Shopify will handle proper formatting server-side
    const dollars = (cents / 100).toFixed(2);
    const symbol = currency === 'CAD' ? 'CA$' : '$';
    return `${symbol}${dollars}`;
  }

  // Track if a claim is in progress to prevent double-clicks
  let claimInProgress = false;
  let lastClickTime = 0;
  const CLICK_DEBOUNCE_MS = 500; // Prevent rapid clicks

  async function handleClaimClick(event) {
    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();
    
    // Debounce rapid clicks (especially important for touch events)
    const now = Date.now();
    if (now - lastClickTime < CLICK_DEBOUNCE_MS) {
      return;
    }
    lastClickTime = now;
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;

    if (!variantId) {
      console.error('[free-gift-claim] No variant ID found');
      return;
    }

    // Prevent double-clicks (especially important on mobile)
    if (claimInProgress) {
      return;
    }

    // Check if button is already processing
    if (button.getAttribute('aria-disabled') === 'true' || button.disabled) {
      return;
    }

    // Set flag and disable button
    claimInProgress = true;
    button.setAttribute('aria-disabled', 'true');
    button.disabled = true;
    button.classList.add('loading');
    
    const buttonSpan = button.querySelector('span');
    const originalText = buttonSpan ? buttonSpan.textContent : button.textContent;
    if (buttonSpan) {
      buttonSpan.textContent = 'Adding...';
    } else {
      button.textContent = 'Adding...';
    }

    try {
      // Get cart drawer to update sections
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerItems = document.querySelector('cart-drawer-items');

      // Prepare form data similar to product-form.js
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cartDrawer) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      // Use fetchConfig from global.js (same pattern as product-form.js)
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      // Add product to cart
      const response = await fetch(routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        // Error adding product
        console.error('[free-gift-claim] Error adding product:', data.description || data.message);
        claimInProgress = false;
        button.removeAttribute('aria-disabled');
        button.disabled = false;
        button.classList.remove('loading');
        const buttonSpan = button.querySelector('span');
        if (buttonSpan) {
          buttonSpan.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
        alert(data.description || 'Unable to add free gift. Please try again.');
        return;
      }

      // Success - update cart drawer
      claimInProgress = false; // Reset flag on success

      if (cartDrawer && data.sections) {
        // Publish cart update event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'free-gift-claim',
            productVariantId: variantId,
            cartData: data,
          });
        }

        // Render cart drawer contents
        cartDrawer.renderContents(data);
      } else if (cartDrawerItems) {
        // Fallback: refresh cart drawer items
        cartDrawerItems.onCartUpdate();
      } else {
        // Last resort: reload page
        window.location.reload();
      }
    } catch (error) {
      console.error('[free-gift-claim] Error:', error);
      claimInProgress = false;
      button.removeAttribute('aria-disabled');
      button.disabled = false;
      button.classList.remove('loading');
      const buttonSpan = button.querySelector('span');
      if (buttonSpan) {
        buttonSpan.textContent = originalText;
      } else {
        button.textContent = originalText;
      }
      alert('Unable to add free gift. Please try again.');
    }
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFreeGiftClaim);
  } else {
    initFreeGiftClaim();
  }

  // Re-initialize when cart drawer updates (for dynamically added buttons)
  // Reset claim flag and re-attach listeners to new buttons
  document.addEventListener('pdm-cartUpdated', () => {
    setTimeout(() => {
      claimInProgress = false; // Reset flag after cart update
      initFreeGiftClaim();
      // Reset lastCartTotal to force update after re-render
      lastCartTotal = null;
    }, 100);
  });

  // Also listen for cart updates via pub/sub
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      setTimeout(() => {
        claimInProgress = false; // Reset flag after cart update
        initFreeGiftClaim();
        // Reset lastCartTotal to force update after re-render
        lastCartTotal = null;
      }, 100);
    });
  }

  // Watch for when cart drawer opens and initialize buttons
  // This handles the case when cart drawer opens with content already rendered
  function setupCartDrawerWatchers() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Watch for when drawer becomes active/visible
      const drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isActive = cartDrawer.classList.contains('active');
            if (isActive) {
              // Drawer just opened, initialize buttons after a short delay
              setTimeout(() => {
                initFreeGiftClaim();
              }, 200);
            }
          }
        });
      });
      
      drawerObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Also watch for buttons being added to the DOM (for initial render)
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      const buttonObserver = new MutationObserver((mutations) => {
        let hasNewButtons = false;
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) { // Element node
                // Check if this node or its children contain claim buttons
                if (node.classList && node.classList.contains('claim-free-gift-btn')) {
                  hasNewButtons = true;
                } else if (node.querySelector && node.querySelector('.claim-free-gift-btn')) {
                  hasNewButtons = true;
                }
              }
            });
          }
        });
        
        if (hasNewButtons) {
          setTimeout(() => {
            initFreeGiftClaim();
          }, 150);
        }
      });
      
      buttonObserver.observe(cartDrawerItems, {
        childList: true,
        subtree: true
      });
    }
  }

  // Setup watchers after initial initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartDrawerWatchers);
  } else {
    setupCartDrawerWatchers();
  }

  // Update callout when cart items change (quantity updates, removals, etc.)
  // Use a more targeted approach - only watch for actual content changes
  const cartDrawerItemsForCallout = document.querySelector('cart-drawer-items');
  if (cartDrawerItemsForCallout) {
    let observerTimeout = null;
    const calloutObserver = new MutationObserver((mutations) => {
      // Only update if the mutations are significant (not just attribute changes)
      const hasSignificantChanges = mutations.some(mutation => 
        mutation.type === 'childList' && mutation.addedNodes.length > 0
      );
      
      if (hasSignificantChanges) {
        if (observerTimeout) {
          clearTimeout(observerTimeout);
        }
        observerTimeout = setTimeout(() => {
          updateCalloutState();
        }, 200);
      }
    });
    
    calloutObserver.observe(cartDrawerItemsForCallout, {
      childList: true,
      subtree: false, // Only watch direct children, not deep subtree
    });
  }
})();
